import React, { useState, useEffect } from 'react';
import { categoriesService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  FolderOpen, 
  Plus, 
  Pencil, 
  Trash2, 
  RefreshCw,
  Search,
  FolderPlus
} from 'lucide-react';
import { toast } from 'sonner';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Form states
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editCategoryName, setEditCategoryName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await categoriesService.getAll();
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreate = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Digite o nome da categoria');
      return;
    }

    setIsSubmitting(true);
    try {
      await categoriesService.create(newCategoryName.trim());
      toast.success('Categoria criada com sucesso!');
      setNewCategoryName('');
      setCreateModalOpen(false);
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Erro ao criar categoria');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!editCategoryName.trim()) {
      toast.error('Digite o nome da categoria');
      return;
    }

    setIsSubmitting(true);
    try {
      await categoriesService.update(selectedCategory.id, editCategoryName.trim());
      toast.success('Categoria atualizada com sucesso!');
      setEditModalOpen(false);
      setSelectedCategory(null);
      setEditCategoryName('');
      fetchCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Erro ao atualizar categoria');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;

    setIsSubmitting(true);
    try {
      await categoriesService.delete(selectedCategory.id);
      toast.success('Categoria excluída com sucesso!');
      setDeleteModalOpen(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Erro ao excluir categoria');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (category) => {
    setSelectedCategory(category);
    setEditCategoryName(category.nome);
    setEditModalOpen(true);
  };

  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setDeleteModalOpen(true);
  };

  const filteredCategories = categories.filter(cat =>
    cat.nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="categories-loading">
        <RefreshCw className="w-8 h-8 text-green-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="categories-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white font-['Manrope']">
            Categorias
          </h1>
          <p className="text-zinc-400 mt-1">
            Gerencie suas categorias de lançamentos
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={fetchCategories}
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:border-green-500 hover:text-green-400"
            data-testid="refresh-categories-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button
            onClick={() => setCreateModalOpen(true)}
            className="bg-green-600 hover:bg-green-500 text-white shadow-[0_0_15px_rgba(34,197,94,0.4)]"
            data-testid="new-category-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Categoria
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          type="text"
          placeholder="Buscar categorias..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-white placeholder:text-zinc-600"
          data-testid="search-categories-input"
        />
      </div>

      {/* Categories Grid */}
      {filteredCategories.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredCategories.map((category) => (
            <Card 
              key={category.id}
              className="bg-zinc-900/50 border border-white/10 rounded-xl group hover:border-green-500/50 transition-colors"
              data-testid={`category-card-${category.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/20">
                      <FolderOpen className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium truncate max-w-[150px]">
                        {category.nome}
                      </h3>
                      <p className="text-xs text-zinc-500">ID: {category.id}</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(category)}
                    className="flex-1 hover:bg-green-500/10 hover:text-green-400 text-zinc-400"
                    data-testid={`edit-category-${category.id}`}
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDeleteModal(category)}
                    className="flex-1 hover:bg-red-500/10 hover:text-red-400 text-zinc-400"
                    data-testid={`delete-category-${category.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Excluir
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-zinc-900/50 border border-white/10 rounded-xl">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 rounded-full bg-zinc-800/50 mb-4">
              <FolderPlus className="w-8 h-8 text-zinc-500" />
            </div>
            <h3 className="text-white font-medium text-lg mb-2">
              {searchTerm ? 'Nenhuma categoria encontrada' : 'Nenhuma categoria criada'}
            </h3>
            <p className="text-zinc-500 text-sm mb-6 text-center max-w-md">
              {searchTerm 
                ? 'Tente buscar por outro termo' 
                : 'Crie sua primeira categoria para organizar seus lançamentos'
              }
            </p>
            {!searchTerm && (
              <Button
                onClick={() => setCreateModalOpen(true)}
                className="bg-green-600 hover:bg-green-500 text-white"
                data-testid="empty-state-new-category-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Categoria
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Nova Categoria</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Crie uma nova categoria para organizar seus lançamentos
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="category-name" className="text-zinc-300">
                Nome da categoria
              </Label>
              <Input
                id="category-name"
                data-testid="new-category-name-input"
                placeholder="Ex: Alimentação, Transporte, Lazer..."
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="bg-zinc-950/50 border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-white placeholder:text-zinc-600"
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateModalOpen(false)}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-500 text-white"
              data-testid="submit-new-category-btn"
            >
              {isSubmitting ? 'Criando...' : 'Criar Categoria'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Editar Categoria</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Altere o nome da categoria
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-category-name" className="text-zinc-300">
                Nome da categoria
              </Label>
              <Input
                id="edit-category-name"
                data-testid="edit-category-name-input"
                value={editCategoryName}
                onChange={(e) => setEditCategoryName(e.target.value)}
                className="bg-zinc-950/50 border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-white placeholder:text-zinc-600"
                onKeyDown={(e) => e.key === 'Enter' && handleEdit()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleEdit}
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-500 text-white"
              data-testid="submit-edit-category-btn"
            >
              {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-['Manrope']">
              Excluir categoria?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Tem certeza que deseja excluir a categoria "{selectedCategory?.nome}"? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-500 text-white"
              data-testid="confirm-delete-category-btn"
            >
              {isSubmitting ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CategoriesPage;
